import React, { useCallback, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { KibModal } from '@chewy/kib-overlays-react'

import { PaymentMethod } from '~/constants/addPaymentDialogV2Constants'

import { PaymentControls } from '../../add-payment-dialog-v2/components/PaymentControls'
import { usePaymentTransactionStatus } from '../../add-payment-dialog-v2/hooks/usePaymentTransactionStatus'
import { PaymentSuccessScreenTitle } from '../../add-payment-dialog-v2/screens/PaymentSuccessScreenTitle'
import { RefundFlowState } from './constants/refundDialogV2Constants'
import { useRefundDialog } from './context/RefundDialogProvider'
import { IssueRefundScreen } from './screens/IssueRefundScreen'
import { RefundProcessingScreen } from './screens/RefundProcessingScreen'
import { RefundSuccessScreen } from './screens/RefundSuccessScreen'
import { calculateRefundableAmount } from './utils/refundCalculations'
import { isRefundTransactionProcessing } from './utils/refundTransactionUtils'

import '@chewy/kib-overlays-styles/dist/styles/kib-modal.css'
import '@chewy/kib-transition-styles/dist/kib-transition.css'
import styles from './RefundDialogV2.module.scss'

interface RefundDialogV2Props {
  isOpen: boolean
  onClose: () => void
}

interface StateConfig {
  controls: JSX.Element
  screen: JSX.Element
  title: string | JSX.Element
}

export const RefundDialogV2 = ({ isOpen, onClose }: RefundDialogV2Props) => {
  const { t } = useTranslation(['Dialogs', 'Common', 'Invoices'])
  const { actions, dialogState, isLoading, refundInvoice } = useRefundDialog()
  const handleClose = useCallback(() => {
    actions.resetDialog()
    onClose()
  }, [actions, onClose])

  const isPaymentTerminalRequired = useMemo(
    () =>
      dialogState.refundMethod === PaymentMethod.CREDIT_DEBIT &&
      dialogState.unlinked,
    [dialogState.refundMethod, dialogState.unlinked],
  )

  const isPaymentTerminalMissing = useMemo(
    () => !dialogState.selectedPaymentTerminal && isPaymentTerminalRequired,
    [dialogState.selectedPaymentTerminal, isPaymentTerminalRequired],
  )
  // Calculate refundable amount
  const refundableAmount = calculateRefundableAmount(refundInvoice)

  const isSubmitButtonDisabled = useMemo(
    () =>
      !dialogState.selectedPayment ||
      refundableAmount <= 0 ||
      isPaymentTerminalMissing,
    [dialogState.selectedPayment, refundableAmount, isPaymentTerminalMissing],
  )

  const { transaction } = usePaymentTransactionStatus(
    dialogState.currentState === RefundFlowState.REFUND_PROCESSING
      ? dialogState.transactionId || ''
      : '',
  )

  const canCancel = useMemo(
    () =>
      dialogState.currentState === RefundFlowState.REFUND_PROCESSING &&
      transaction &&
      isRefundTransactionProcessing(transaction) &&
      Boolean(dialogState.externalDeviceId) &&
      !dialogState.isCancelling,
    [
      dialogState.currentState,
      dialogState.externalDeviceId,
      dialogState.isCancelling,
      transaction,
    ],
  )

  // Auto-close when refund is fully processed (refundable amount becomes 0)
  useEffect(() => {
    if (
      refundableAmount === 0 &&
      isOpen &&
      dialogState.currentState === RefundFlowState.ISSUE_REFUND &&
      !isLoading
    ) {
      handleClose()
    }
  }, [
    refundableAmount,
    dialogState.currentState,
    handleClose,
    isOpen,
    isLoading,
  ])

  const refundStateConfig: Record<RefundFlowState, StateConfig> = useMemo(
    () => ({
      [RefundFlowState.ISSUE_REFUND]: {
        screen: <IssueRefundScreen />,
        title: t('Dialogs:REFUND_DIALOG_V2.ISSUE_REFUND'),
        controls: (
          <PaymentControls
            disabled={isSubmitButtonDisabled}
            emphasis="primary"
            isLoading={isLoading}
            primaryAction={() => {
              if (
                dialogState.linked &&
                dialogState.refundMethod === PaymentMethod.CREDIT_DEBIT
              ) {
                actions.submitLinkedRefund(dialogState)
              } else if (
                dialogState.unlinked &&
                dialogState.refundMethod === PaymentMethod.CREDIT_DEBIT
              ) {
                actions.submitUnlinkedRefund(dialogState)
              } else {
                actions.submitManualRefund(dialogState)
              }
            }}
            primaryText={t('Invoices:SUBMIT_REFUND_ACTION')}
          />
        ),
      },
      [RefundFlowState.REFUND_PROCESSING]: {
        screen: <RefundProcessingScreen />,
        title: t('Common:REFUND_PROCESSING'),
        controls: (
          <PaymentControls
            cancelButton
            disabled={!canCancel}
            emphasis="secondary"
            isLoading={dialogState.isCancelling}
            primaryAction={actions.cancelTransaction}
            primaryText={t('Dialogs:ADD_PAYMENT_DIALOG_V2.CANCEL_TRANSACTION')}
          />
        ),
      },
      [RefundFlowState.REFUND_SUCCESS]: {
        screen: <RefundSuccessScreen />,
        title: (
          <PaymentSuccessScreenTitle
            title={t('Dialogs:REFUND_DIALOG_V2.REFUND_SUCCESS')}
          />
        ),
        controls: (
          <PaymentControls
            emphasis="secondary"
            isLoading={false}
            primaryAction={handleClose}
            primaryText={t('Common:RETURN_TO_INVOICE')}
          />
        ),
      },
    }),
    [
      handleClose,
      actions,
      t,
      isSubmitButtonDisabled,
      dialogState,
      isPaymentTerminalRequired,
      isPaymentTerminalMissing,
      isLoading,
      canCancel,
      dialogState.isCancelling,
    ],
  )

  const currentConfig = refundStateConfig[dialogState.currentState]
  const controls = useCallback(() => currentConfig.controls, [currentConfig])
  return (
    <KibModal
      className={styles.refundDialogV2Container}
      controls={controls}
      open={isOpen}
      title={currentConfig.title}
      onClose={handleClose}
    >
      {currentConfig.screen}
    </KibModal>
  )
}
